import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Col, Popconfirm, Row } from 'antd';

import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import Drawer from '@/views/components/Modals/Drawer';

import { ReactComponent as BoxIcon } from '../../../../assets/icons/box-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as ExportIcon } from '../../../../assets/icons/export-icon.svg';
import { ReactComponent as TableIcon } from '../../../../assets/icons/log-file.svg';
import { ReactComponent as SettingsIcon } from '../../../../assets/icons/Settings.svg';
import { SEARCH_VIEW_TYPES } from '../../../../contants/common';
import { APP_ROUTES } from '../../../../contants/routes';
import { setOCClaimViewType, setSelectedOverChargeClaims } from '../../../../redux/Claims/OverCharge/OverChargeActions';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import IconButton from '../../../components/Buttons/IconButton';
import TextButton from '../../../components/Buttons/TextButton';
import SvgConverter from '../../../components/Common/SvgConverter';
import FormCheckBox from '../../../components/Inputs/FormCheckBox';
import OverChargeClaimsSorting from './Components/OverChargeClaimsSorting';
import OverChargeExportClaimsModal from './Export/OverChargeExportClaimsModal';
import { useDeleteOCClaims, useGetOCClaimsList } from './hooks';
import OCClaimTableViewFieldsDrawer from './TableViewFieldsOptions/OCClaimTableViewFieldsDrawer';

const OverChargeClaimsHeader = () => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const dispatch = useDispatch();
  const [tableViewFieldsDrawer, setTableViewFieldsDrawer] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);
  const { selectedClaims, claimsFilters, claimViewMode } = useSelector((state) => state.overChargeClaims);
  const { ...searchFilters } = claimsFilters;
  const { data } = useGetOCClaimsList({
    companyId: selectedCompany?.companyId,
    enabled: Boolean(selectedCompany?.companyId),
    searchFilters,
  });
  const { mutate: deleteClaims } = useDeleteOCClaims();
  const claimsList = useMemo(() => data?.flatClaims ?? [], [data?.flatClaims]);

  const actionButtonsDisabled = useMemo(() => selectedClaims.length <= 0, [selectedClaims?.length]);

  const isSelectAll = useMemo(() => {
    return (
      claimsList.length > 0 &&
      selectedClaims.length > 0 &&
      claimsList.length === selectedClaims.length &&
      claimsList.every((claim) => selectedClaims.includes(claim.fbocKey))
    );
  }, [claimsList, selectedClaims]);

  const handleCheckBoxClick = (val) => {
    if (val) {
      if (val.target.checked) {
        dispatch(setSelectedOverChargeClaims(claimsList.map(({ fbocKey }) => fbocKey)));
      } else {
        dispatch(setSelectedOverChargeClaims([]));
      }
    }
  };

  const handleDelete = () => {
    deleteClaims({
      fbocKeys: selectedClaims.join(','),
      filters: claimsFilters,
      onSuccess: () => dispatch(setSelectedOverChargeClaims([])),
    });
  };

  const handleShipmentViewToggle = () => {
    dispatch(
      setOCClaimViewType(claimViewMode === SEARCH_VIEW_TYPES.CARD ? SEARCH_VIEW_TYPES.TABLE : SEARCH_VIEW_TYPES.CARD),
    );
  };

  return (
    <div className="px-15 py-10">
      <Row justify="space-between" align="middle">
        <Col className="flex-1">
          <Row align="middle" gutter={20}>
            <Col>
              <FormCheckBox
                className="m-0 pl-1"
                checkBoxClass="normal-checkbox"
                onChange={handleCheckBoxClick}
                checked={isSelectAll}
                label={<span className="ml-7 f-14 text-black">{t('translation:default.fields.selectAll')}</span>}
              />
            </Col>
            <Col>
              <Button
                type="default"
                onClick={() => handleCheckBoxClick({ target: { checked: false } })}
                className="clear-selection-button p-0 ml-n5"
              >
                {t('translation:default.buttons.clearSelection')}
              </Button>
            </Col>
            <Col className="flex-1">
              <OverChargeClaimsSorting />
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={15} align="middle">
            <Col className="mr-5">
              <Link
                to={`${APP_ROUTES.OVERCHARGE_CLAIMS_CREATE}${
                  claimsFilters.carrierKey ? `?carrierKey=${claimsFilters.carrierKey}` : ''
                }`}
              >
                <Button>{t('translation:default.fields.create')}</Button>
              </Link>
            </Col>
            <Col>
              <Popconfirm
                placement="topRight"
                title={t('translation:default.fields.deleteConfirm.title')}
                onConfirm={handleDelete}
                okText={t('translation:default.options.yes')}
                cancelText={t('translation:default.buttons.cancel')}
              >
                <TextButton icon={<SvgConverter Icon={DeleteIcon} />} disabled={actionButtonsDisabled}>
                  <label className="ml-5">{t('translation:default.buttons.delete')}</label>
                </TextButton>
              </Popconfirm>
            </Col>
            <Col>
              <TextButton
                disabled={actionButtonsDisabled}
                icon={<SvgConverter Icon={ExportIcon} />}
                onClick={() => setExportOpen(true)}
              >
                <label className="ml-5">{t('translation:claims.printResults')}</label>
              </TextButton>
            </Col>
            <Col>
              <IconButton onClick={handleShipmentViewToggle}>
                <SvgConverter
                  Icon={claimViewMode === SEARCH_VIEW_TYPES.CARD ? TableIcon : BoxIcon}
                  color="header"
                  width={22}
                  height={22}
                />
              </IconButton>
            </Col>
            {claimViewMode === SEARCH_VIEW_TYPES.TABLE ? (
              <Col>
                <TextButton
                  onClick={() => setTableViewFieldsDrawer(true)}
                  icon={<SvgConverter Icon={SettingsIcon} height={22} width={22} />}
                >
                  <label className="ml-0">Column Options</label>
                </TextButton>
              </Col>
            ) : null}
          </Row>
        </Col>
      </Row>
      <OverChargeExportClaimsModal open={exportOpen} setOpen={setExportOpen} isSelectAll={isSelectAll} />
      <Drawer
        open={tableViewFieldsDrawer}
        onClose={() => setTableViewFieldsDrawer(false)}
        headerContents={<PrimaryLabel className="text-white f-16">Column Options</PrimaryLabel>}
        width="400px"
        destroyOnHidden
      >
        <OCClaimTableViewFieldsDrawer onClose={() => setTableViewFieldsDrawer(false)} />
      </Drawer>
    </div>
  );
};

export default OverChargeClaimsHeader;
